import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isLoggedIn, clearToken,
  fetchInternshipApplications, updateInternshipApplicationStatus, deleteInternshipApplication,
} from '../../api/client';
import AdminNav from './AdminNav';
import './Admin.css';

function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path d="M4 6h12M8 6V4.5a1 1 0 011-1h2a1 1 0 011 1V6m-7 0l.7 9.2a1.5 1.5 0 001.5 1.4h4.6a1.5 1.5 0 001.5-1.4L15 6"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const STATUSES = ['New', 'Reviewed', 'Contacted'];

export default function AdminInternships() {
  const navigate = useNavigate();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);

  useEffect(() => {
    if (!isLoggedIn()) { navigate('/admin', { replace: true }); return; }
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = () => {
    setLoading(true);
    fetchInternshipApplications()
      .then((data) => {
        // Defensive: only ever trust an actual array into state. If the
        // API ever returns something unexpected (null, an error object,
        // etc.) fall back to an empty list instead of letting items.length
        // crash the whole page.
        setItems(Array.isArray(data) ? data : []);
        if (!Array.isArray(data)) {
          setToast({ type: 'err', text: 'Unexpected response while loading applications.' });
        }
      })
      .catch(() => {
        setItems([]);
        setToast({ type: 'err', text: 'Could not load applications.' });
      })
      .finally(() => setLoading(false));
  };

  const handleLogout = () => { clearToken(); navigate('/admin'); };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInternshipApplicationStatus(id, status);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Could not update status.' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application? This cannot be undone.')) return;
    try {
      await deleteInternshipApplication(id);
      setToast({ type: 'ok', text: 'Deleted.' });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      setToast({ type: 'err', text: err.message || 'Delete failed.' });
    }
  };

  return (
    <div className="adm">
      <div className="adm-topbar">
        <h1 className="adm-topbar__title">Internship Applications — Admin</h1>
        <button className="adm-logout" onClick={handleLogout}>Sign Out</button>
      </div>

      <AdminNav active="internships" />

      <div className="adm-body">
        <div className="adm-card">
          <h2 className="adm-card__title">All Applications ({items.length})</h2>

          {toast && (
            <div className={`adm-toast adm-toast--${toast.type}`}>{toast.text}</div>
          )}

          {loading && <p className="adm-empty">Loading…</p>}
          {!loading && items.length === 0 && <p className="adm-empty">No applications yet.</p>}

          {!loading && items.length > 0 && (
            <div className="adm-list">
              {items.map((item) => (
                <div className="adm-item" key={item.id}>
                  <div className="adm-item__body">
                    <p className="adm-item__title">{item.name} — {item.domain}</p>
                    <p className="adm-item__meta">
                      {item.email} · {item.phone} · {new Date(item.created_at).toLocaleDateString()}
                    </p>
                    {item.message && <p className="adm-item__meta">{item.message}</p>}
                  </div>
                  <div className="adm-item__actions">
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      style={{ fontSize: 12, borderRadius: 6, padding: '4px 6px' }}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => handleDelete(item.id)} aria-label="Delete"><IconTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}